import { useCallback, useMemo, useState } from "react";
import { FEATURE_FLAGS } from "@cede/types";
import { formatBalance } from "@cede/utils";
import { ClaimButton } from "./ClaimButton";
import { RewardsModalContent } from "./RewardsModalContent";
import { getDayLabel } from "@/components/features/leaderboards/epoch-leaderboard/utils";
import { useTelegramContext } from "@/contexts/TelegramContext";
import { ClaimRewardsError, RewardEpochDetails, useAllCampaigns, useClaimTTEHTERewards } from "@/hooks";
import { useMerklRewards } from "@/hooks/campaign/useMerklRewards";
import { useCashbackStatus } from "@/hooks/cashback/useCashbackStatus";
import { useFeatureFlagIsOn } from "@/hooks/feature-flags";
import { CLAIM_REWARDS_ERROR_TYPE } from "@/hooks/user/useClaimRewards";
import { cn } from "@/lib/utils";
import { useCashbackDialogStore } from "@/store/cashbackDialogStore";
import Decimal from "decimal.js";
import { useSnackbar } from "notistack";

type ClaimRewardsProps = {
  fullWidth?: boolean;
  buttonStyleVariant?: "primary" | "secondary" | "main" | "tertiary";
  buttonClassName?: string;
  buttonSize?: "small" | "medium" | "large";
};

export const ClaimRewards = ({
  fullWidth = false,
  buttonStyleVariant,
  buttonClassName,
  buttonSize,
}: ClaimRewardsProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const [claimModalOpen, setClaimModalOpen] = useState(false);
  const { isTMA } = useTelegramContext();
  const isClaimsEnabled = useFeatureFlagIsOn(FEATURE_FLAGS.CLAIMS_HUB_ENABLE);
  const { handleOpen: openCashback } = useCashbackDialogStore();
  const { status: cashbackStatus } = useCashbackStatus();

  const cashbackClaimable = useMemo(() => {
    if (!cashbackStatus?.claimable?.amount) return new Decimal(0);
    return new Decimal(cashbackStatus.claimable.amount);
  }, [cashbackStatus?.claimable?.amount]);

  const {
    totalRewardAmountFiat,
    rewardChainIds,
    address,
    rewardDetails,
    claimReward,
    isLoading,
    isClaimCooldownActive,
    claimableSoonRewardDetails,
    claimablesData,
    batchProgress,
    totalBatches,
    chainClaimProgress,
  } = useClaimTTEHTERewards({
    onClaimSuccess: () => {
      setClaimModalOpen(false);
      enqueueSnackbar("Rewards claimed successfully", { variant: "success" });
    },
    onClaimError: (error: ClaimRewardsError) => {
      if (error.type === CLAIM_REWARDS_ERROR_TYPE.USER_REJECTED) return;
      enqueueSnackbar(error.message, { variant: "error" });
    },
  });

  const { campaigns } = useAllCampaigns();
  // The API only accepts USDT for Merkl campaigns, so Merkl amounts add 1:1 to dollars.
  const { totalClaimable: merklClaimable } = useMerklRewards();
  const campaignRewardsClaimable = new Decimal(totalRewardAmountFiat).plus(merklClaimable);

  const populateRewardDetails = useCallback(
    (rewardDetailsMap: Map<string, RewardEpochDetails[]>) => {
      const result: {
        campaignData: {
          id: string;
          name: string;
          icon: string | null;
          title: string;
        };
        rewardDetails: (RewardEpochDetails & { epochLabel: string })[];
      }[] = [];

      for (const epochs of rewardDetailsMap.values()) {
        const populatedRewardDetails = epochs
          .map((epochData) => {
            const epochLabel = getDayLabel(epochData.activeEpochId, epochData.epochStart, epochData.epochEnd);
            return {
              ...epochData,
              epochLabel,
            };
          })
          .filter((v) => !!v);

        result.push({
          campaignData: epochs[0]?.campaignData || { id: "0", name: "", icon: null, title: "" },
          rewardDetails: populatedRewardDetails,
        });
      }
      return result;
    },
    [campaigns],
  );

  const populatedRewardsDetails = useMemo(() => {
    return populateRewardDetails(rewardDetails);
  }, [rewardDetails, populateRewardDetails]);

  const populatedClaimableSoonRewardsDetails = useMemo(() => {
    return populateRewardDetails(claimableSoonRewardDetails);
  }, [claimableSoonRewardDetails, populateRewardDetails]);

  const rewardText = isTMA
    ? ""
    : isClaimsEnabled
      ? campaignRewardsClaimable.gt(0)
        ? `$${formatBalance(campaignRewardsClaimable, { roundForFiat: true })}`
        : "$0"
      : "";
  const claimButtonVariant = !isClaimsEnabled ? "maintenance" : isLoading ? "loading" : isTMA ? "simple" : "animated";

  return (
    <>
      <ClaimButton
        text={isClaimsEnabled ? "Claim Rewards" : "Rewards Coming Soon"}
        variant={claimButtonVariant}
        buttonVariant={buttonStyleVariant}
        buttonSize={buttonSize}
        reward={rewardText}
        onClick={() => isClaimsEnabled && setClaimModalOpen(true)}
        fullWidth={fullWidth}
        className={cn(!fullWidth && "w-fit self-start", buttonClassName)}
      />
      <RewardsModalContent
        open={claimModalOpen}
        onClose={() => setClaimModalOpen(false)}
        claimableSoonRewardDetails={populatedClaimableSoonRewardsDetails}
        rewardDetails={populatedRewardsDetails}
        vaultReward={formatBalance(totalRewardAmountFiat, { roundForFiat: true })}
        totalRewardFiat={campaignRewardsClaimable}
        rewardChainIds={rewardChainIds}
        address={address || ""}
        onClaim={() => claimReward(undefined)}
        isLoading={isLoading}
        isTMA={isTMA}
        isClaimCooldownActive={isClaimCooldownActive}
        hasClaimableItems={(claimablesData?.length ?? 0) > 0}
        hasMerklClaimable={merklClaimable.gt(0)}
        batchProgress={batchProgress}
        totalBatches={totalBatches}
        chainClaimProgress={chainClaimProgress}
        cashbackSummary={{
          claimableAmount: cashbackClaimable.toNumber(),
          currency: cashbackStatus?.claimable?.currency ?? null,
          onOpenCashback: openCashback,
        }}
      />
    </>
  );
};
